import { useEffect, useRef, useState } from "react";
import axios from "axios";
import confetti from "canvas-confetti";
import {
  LayoutDashboard,
  MessageCircle,
  TrendingUp,
  ClipboardList,
  Users,
  Package,
  Briefcase,
  Settings,
  Bot,
  Trash2,
  Banknote,
  Receipt,
  FileText,
  Download,
  Share2,
  Wallet,
  Landmark,
  BarChart3,
  Send,
  CheckCheck,
  HelpCircle,
  CalendarDays,
  AlertTriangle,
  Trophy,
  ArrowUpRight,
  ArrowDownRight,
  CircleDollarSign,
  ArrowDownLeft,
} from "lucide-react";
import "@/App.css";
import RecordPaymentModal from "@/components/RecordPaymentModal";
import VoiceButton from "@/components/VoiceButton";
import CustomersScreen from "@/components/CustomersScreen";
import ReportsScreen from "@/components/ReportsScreen";
import SettingsScreen from "@/components/SettingsScreen";
import InventoryScreen from "@/components/InventoryScreen";
import OnboardingFlow from "@/components/OnboardingFlow";
import AuthScreen from "@/components/AuthScreen";
import PreAuthFlow from "@/components/PreAuthFlow";
import { AnimatedCounter, KipaIcon } from "@/components/kipa";
import { getQuickReplyDisplay } from "@/lib/quickReplyIcons";
import { isFirstLoginPending, markFirstLoginComplete } from "@/lib/firstLogin";
import { useAuth } from "@/contexts/AuthContext";
import { hasPendingOnboarding, hasSeenWelcome, markWelcomeSeen } from "@/lib/onboardingFlags";
import PushPromptSheet from "@/components/PushPromptSheet";
import LoginIntentPrompt from "@/components/LoginIntentPrompt";
import SalesWizard from "@/components/SalesWizard";
import ExpenseWizard from "@/components/ExpenseWizard";
import SetupReminderBanner from "@/components/SetupReminderBanner";
import ChatMessageBody from "@/components/ChatMessageBody";
import EmptyState from "@/components/EmptyState";
import { consumeFreshLogin, readSavedTab, saveActiveTab, clearSavedTab } from "@/lib/sessionFlags";
import { useAccountant } from "@/contexts/AccountantContext";
import { getCurrencySymbol } from "@/lib/currencies";
import { formatAppDateShort } from "@/lib/formatDate";
import { openAuthenticatedPdf } from "@/lib/downloadExport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ════════════════════════════════════════════════════════════════════
// Chat (WhatsApp-style) — primary screen
// ════════════════════════════════════════════════════════════════════
function ChatScreen({ onAfterSend, bootIntent, onOpenSalesWizard, onOpenExpenseWizard, chatReloadKey }) {
  const { name: accountantName, settings, refresh, fmt, currency } = useAccountant();
  const [messages, setMessages] = useState([]);
  const [welcomeMsg, setWelcomeMsg] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingWelcome, setLoadingWelcome] = useState(true);
  const [payInvoice, setPayInvoice] = useState(null);
  const [productSuggestions, setProductSuggestions] = useState([]);
  const bodyRef = useRef(null);
  const bootApplied = useRef(false);

  useEffect(() => {
    if (!bootIntent || bootApplied.current) return;
    if (bootIntent === "sales") {
      onOpenSalesWizard?.();
    } else if (bootIntent === "expenses") {
      onOpenExpenseWizard?.();
    }
    bootApplied.current = true;
  }, [bootIntent, onOpenSalesWizard, onOpenExpenseWizard]);

  const loadMessages = async () => {
    try {
      const [msgRes, openRes] = await Promise.all([
        axios.get(`${API}/chat/messages`),
        axios.get(`${API}/accountant/opening-message`),
      ]);
      setMessages(msgRes.data);
      setWelcomeMsg(openRes.data.message);
    } catch (e) {
      console.error(e);
    }
    // Simulate typing for 1.5s on first load (only if no messages exist yet)
    setTimeout(() => setLoadingWelcome(false), 1500);
  };

  useEffect(() => {
    loadMessages();
  }, [chatReloadKey]);

  useEffect(() => {
    const q = text.trim();
    const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
    const serverSuggestions = lastAssistant?.data?.product_suggestions || [];
    const collecting = lastAssistant?.data?.pending_context?.step === "collecting";

    if (q.length < 1 && !collecting) {
      setProductSuggestions([]);
      return undefined;
    }
    if (q.length < 1 && collecting && serverSuggestions.length) {
      setProductSuggestions(serverSuggestions);
      return undefined;
    }
    if (q.length < 2 && !/\bof\s+\S/i.test(q)) {
      setProductSuggestions(serverSuggestions.length ? serverSuggestions : []);
      return undefined;
    }
    const t = setTimeout(() => {
      axios.get(`${API}/inventory/suggest`, { params: { q } })
        .then((r) => {
          const fromApi = r.data.suggestions || [];
          setProductSuggestions(fromApi.length ? fromApi : serverSuggestions);
        })
        .catch(() => setProductSuggestions(serverSuggestions));
    }, 250);
    return () => clearTimeout(t);
  }, [text, messages]);

  useEffect(() => {
    axios.post(`${API}/accountant/retention-check`).catch(() => {});
  }, []);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, sending, loadingWelcome]);

  const send = async (raw) => {
    const msg = (raw ?? text).trim();
    if (!msg || sending) return;

    // If there's a pending deposit-amount prompt, parse a number and route
    // to /api/chat/action instead of the normal AI flow (Fix 2).
    const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
    const pending = lastAssistant?.data?.pending_context;
    if (pending?.step === "await_deposit_amount") {
      const numMatch = msg.replace(/[^0-9.]/g, "").match(/(\d+(?:\.\d+)?)/);
      if (numMatch) {
        const amount = parseFloat(numMatch[1]);
        setSending(true);
        setText("");
        setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", text: msg, created_at: new Date().toISOString() }]);
        try {
          await axios.post(`${API}/chat/action`, {
            action_id: "deposit_amount",
            context: pending,
            amount,
          });
          const fresh = await axios.get(`${API}/chat/messages`);
          setMessages(fresh.data);
          if (onAfterSend) onAfterSend();
        } catch (e) {
          console.error(e);
        } finally {
          setSending(false);
        }
        return;
      }
    }

    setSending(true);
    setText("");
    const tmpUser = { id: `tmp-${Date.now()}`, role: "user", text: msg, created_at: new Date().toISOString() };
    setMessages((m) => [...m, tmpUser]);
    try {
      // Special: "What can you do?" → capabilities message, no AI call
      if (msg.toLowerCase().includes("what can you do")) {
        const capRes = await axios.get(`${API}/accountant/capabilities-message`);
        setMessages((m) => [...m, {
          id: `cap-${Date.now()}`,
          role: "assistant",
          text: capRes.data.message,
          created_at: new Date().toISOString(),
        }]);
        return;
      }
      const r = await axios.post(`${API}/process-input`, { text: msg });
      const isFirst = (r.data.message || "").includes("__FIRST_TRANSACTION__");

      // Reload chat history from server so we get quick_replies + pending_context
      const fresh = await axios.get(`${API}/chat/messages`);
      setMessages(fresh.data);

      // Celebrate first transaction with confetti
      if (isFirst) {
        fireConfetti();
        await refresh();
      }
      if (onAfterSend) onAfterSend();
    } catch (e) {
      setMessages((m) => [
        ...m,
        { id: `err-${Date.now()}`, role: "assistant", text: "Sorry, I had trouble processing that. Please try again.", created_at: new Date().toISOString() },
      ]);
    } finally {
      setSending(false);
    }
  };

  // Handle quick-reply chip tap (Fix 2)
  const onQuickReply = async (chip, pendingContext) => {
    if (!chip || sending) return;
    setSending(true);
    try {
      // If this chip is a cash/bank selector, send account_type + the step-aware context
      const payload = {
        action_id: chip.id,
        context: pendingContext || {},
      };
      await axios.post(`${API}/chat/action`, payload);
      const fresh = await axios.get(`${API}/chat/messages`);
      setMessages(fresh.data);
      if (onAfterSend) onAfterSend();
    } catch (e) {
      console.error("quick-reply failed", e);
    } finally {
      setSending(false);
    }
  };

  const applyProductSuggestion = (name) => {
    const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
    const pending = lastAssistant?.data?.pending_context;
    const q = text.trim();

    if (pending?.step === "collecting" && !q) {
      send(name);
      return;
    }
    if (/\bof\s*$/i.test(q) || /\bof\s+\S*$/i.test(q)) {
      const base = q.replace(/\bof\s+\S*$/i, "").replace(/\bof\s*$/i, "");
      setText(base ? `${base} of ${name}` : name);
      return;
    }
    setText((prev) => (prev ? `${prev} ${name}` : name));
  };

  const clearChat = async () => {
    if (sending) return;
    if (!window.confirm("Clear this chat? Your sales, stock, and records are not affected.")) return;
    setSending(true);
    try {
      await axios.delete(`${API}/chat/messages`);
      const fresh = await axios.get(`${API}/chat/messages`);
      setMessages(fresh.data);
      setProductSuggestions([]);
      setText("");
      setLoadingWelcome(true);
      const openRes = await axios.get(`${API}/accountant/opening-message`);
      setWelcomeMsg(openRes.data.message);
      setTimeout(() => setLoadingWelcome(false), 800);
      if (onAfterSend) onAfterSend();
    } catch (e) {
      console.error(e);
      alert("Could not clear chat. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const suggestions = [
    { label: "What can you do?", action: () => send("What can you do?"), icon: HelpCircle },
    { label: "Today's summary", action: () => send("Give me today's summary"), icon: CalendarDays },
    { label: "Record a sale", action: () => onOpenSalesWizard?.(), icon: Banknote },
    { label: "I had an expense", action: () => onOpenExpenseWizard?.(), icon: Receipt },
  ];

  const sym = getCurrencySymbol(currency);

  const hasChatHistory = messages.length > 0;

  return (
    <div className="flex flex-col h-full chat-screen" data-testid="chat-screen">
      {/* Chat header */}
      <div className="bg-white px-4 py-3 border-b border-[var(--br)] flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-[var(--color-primary-light)] border border-[var(--color-primary-dim)] flex items-center justify-center">
          <KipaIcon icon={Bot} size="nav" color="var(--color-primary)" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-extrabold text-[15px] text-[var(--tx)] truncate" data-testid="chat-header-name">
            {accountantName}
          </div>
          <div className="text-[11px] text-[var(--color-success)] font-semibold flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[var(--color-success)] inline-block" />
            Online · ready to help
          </div>
        </div>
        <div className="flex gap-1.5 flex-shrink-0 items-center">
          <button
            type="button"
            onClick={clearChat}
            disabled={sending}
            data-testid="clear-chat-btn"
            className="chat-action-btn bg-[var(--sf2)] border-[var(--br)] text-[var(--tx2)]"
          >
            <KipaIcon icon={Trash2} size="sm" />
            Clear chat
          </button>
          <button
            type="button"
            onClick={() => onOpenSalesWizard?.()}
            data-testid="open-sales-wizard"
            className="chat-action-btn bg-[var(--ac)] text-white"
          >
            <KipaIcon icon={Banknote} size="sm" color="#fff" />
            Sale
          </button>
          <button
            type="button"
            onClick={() => onOpenExpenseWizard?.()}
            data-testid="open-expense-wizard"
            className="chat-action-btn bg-[var(--rd)] text-white"
          >
            <KipaIcon icon={Receipt} size="sm" color="#fff" />
            Expense
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={bodyRef} className="flex-1 overflow-y-auto px-3 py-4 chat-thread" data-testid="chat-body">
        <div className="text-center mb-4">
          <span className="chat-date-pill">
            <KipaIcon icon={FileText} size="xs" color="var(--color-text-muted)" />
            Today · Auto-invoicing on
          </span>
        </div>

        {/* Welcome message — typing then reveal */}
        {!hasChatHistory && loadingWelcome && (
          <div className="msg-row bot">
            <div className="chat-avatar"><KipaIcon icon={Bot} size="sm" color="var(--color-primary)" /></div>
            <div className="bubble bot" data-testid="typing-welcome">
              <div className="flex gap-1.5 py-1">
                <span className="typing-dot" />
                <span className="typing-dot" style={{ animationDelay: "0.15s" }} />
                <span className="typing-dot" style={{ animationDelay: "0.3s" }} />
              </div>
            </div>
          </div>
        )}
        {!hasChatHistory && !loadingWelcome && welcomeMsg && (
          <div className="msg-row bot">
            <div className="chat-avatar"><KipaIcon icon={Bot} size="sm" color="var(--color-primary)" /></div>
            <div className="bubble bot" data-testid="welcome-msg">
              <div style={{ whiteSpace: "pre-wrap" }}><ChatMessageBody text={welcomeMsg} /></div>
              <div className="btime">now</div>
            </div>
          </div>
        )}

        {messages.map((m) => {
          const isUser = m.role === "user";
          const txn = m.data?.transaction;
          const inv = m.data?.updated_invoice || m.data?.invoice;
          const bals = m.data?.balances;
          const invBalance = inv ? inv.total - (inv.amount_paid || 0) : 0;
          const canPay = inv && inv.status !== "paid" && invBalance > 0;
          return (
            <div key={m.id} className={`msg-row ${isUser ? "user" : "bot"}`}>
              {!isUser && (
                <div className="chat-avatar"><KipaIcon icon={Bot} size="sm" color="var(--color-primary)" /></div>
              )}
              <div className={`bubble ${isUser ? "user" : "bot"}`} data-testid={`msg-${m.role}`}>
                <ChatMessageBody text={m.text} />
                {!isUser && inv && (
                  <div className="chat-inv-card">
                    <div className="flex justify-between items-center text-[11px] font-bold text-[var(--go)] mb-1">
                      <span className="inline-flex items-center gap-1">
                        <KipaIcon icon={FileText} size="xs" color="var(--color-warning)" />
                        {inv.invoice_number}
                      </span>
                      <span className="uppercase">{inv.status}</span>
                    </div>
                    <div className="text-[12px] text-[var(--tx2)] mb-2">
                      {inv.customer} · {inv.item} · <b>{fmt(inv.total)}</b>
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                      {canPay && (
                        <button
                          type="button"
                          onClick={() => setPayInvoice(inv)}
                          data-testid={`chat-inv-pay-${inv.id}`}
                          className="chat-inv-btn bg-[var(--ac)] text-white"
                        >
                          <KipaIcon icon={CircleDollarSign} size="xs" color="#fff" />
                          Mark paid
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await openAuthenticatedPdf(`${API}/invoices/${inv.id}/pdf`);
                          } catch (e) {
                            alert(e.message || "Could not open PDF.");
                          }
                        }}
                        data-testid={`chat-inv-pdf-${inv.id}`}
                        className="chat-inv-btn bg-white border border-[var(--br)] text-[var(--tx2)]"
                      >
                        <KipaIcon icon={Download} size="xs" />
                        PDF
                      </button>
                      <a
                        href={`https://wa.me/?text=${encodeURIComponent(
                          `Hello ${inv.customer},\n\nHere is your invoice ${inv.invoice_number} for ${inv.item} — total ${fmt(inv.total)}.${
                            inv.status === "paid" ? "\nFully paid, thank you!" : `\nBalance: ${fmt(inv.total - (inv.amount_paid || 0))}`
                          }\n\nSent via KipaBiz.`
                        )}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`chat-inv-wa-${inv.id}`}
                        className="chat-inv-btn bg-[var(--gn)] text-white"
                      >
                        <KipaIcon icon={Share2} size="xs" color="#fff" />
                        Share
                      </a>
                    </div>
                  </div>
                )}
                {!isUser && txn && !inv && (
                  <div className="mt-2 bg-[var(--gl)] border border-[var(--gd)] rounded-xl p-2 text-[11px] text-[var(--tx2)]">
                    <b className="text-[var(--gn)]">{txn.type === "income" ? "+" : "-"}{fmt(txn.amount)}</b> · {txn.category}
                  </div>
                )}
                {!isUser && bals && (
                  <div className="mt-2 bg-[var(--al)] border border-[var(--ad)] rounded-xl p-2 text-[11px] flex flex-wrap gap-x-3 gap-y-1" data-testid="msg-balances">
                    <span className="inline-flex items-center gap-1">
                      <KipaIcon icon={Wallet} size="xs" color="var(--color-primary)" />
                      Cash: <b>{fmt(bals.cash_balance)}</b>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <KipaIcon icon={Landmark} size="xs" color="var(--color-primary)" />
                      Bank: <b>{fmt(bals.bank_balance)}</b>
                    </span>
                  </div>
                )}
                <div className="btime">
                  {isUser && <KipaIcon icon={CheckCheck} size="xs" color="rgba(255,255,255,0.75)" />}
                </div>
              </div>
            </div>
          );
        })}

        {/* Quick-reply chips for the LATEST assistant message (Fix 2) */}
        {(() => {
          const last = [...messages].reverse().find((m) => m.role === "assistant");
          const qr = last?.data?.quick_replies;
          const ctx = last?.data?.pending_context;
          if (!qr || !qr.length || sending) return null;
          return (
            <div className="mt-1 mb-1 flex flex-wrap gap-2" data-testid="quick-replies">
              {qr.map((c) => {
                const { icon: QIcon, label } = getQuickReplyDisplay(c);
                return (
                  <button
                    key={c.id}
                    onClick={() => onQuickReply(c, ctx)}
                    data-testid={`quick-reply-${c.id}`}
                    className="chat-quick-reply"
                  >
                    {QIcon && <KipaIcon icon={QIcon} size="sm" color="var(--color-primary)" />}
                    {label}
                  </button>
                );
              })}
            </div>
          );
        })()}

        {sending && (
          <div className="msg-row bot">
            <div className="chat-avatar"><KipaIcon icon={Bot} size="sm" color="var(--color-primary)" /></div>
            <div className="bubble bot" data-testid="typing-indicator">
              <div className="flex gap-1.5 py-1">
                <span className="typing-dot" />
                <span className="typing-dot" style={{ animationDelay: "0.15s" }} />
                <span className="typing-dot" style={{ animationDelay: "0.3s" }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Suggestion chips */}
      <div className="flex gap-2 overflow-x-auto px-4 py-2 bg-[var(--color-surface)] border-t border-[var(--br)]" style={{ scrollbarWidth: "none" }}>
        {suggestions.map((s, i) => (
          <button
            key={i}
            onClick={s.action}
            disabled={sending}
            data-testid={`chip-${i}`}
            className="chip chat-suggestion-chip"
          >
            <KipaIcon icon={s.icon} size="sm" color="var(--color-primary)" />
            {s.label}
          </button>
        ))}
      </div>

      {productSuggestions.length > 0 && (
        <div className="px-3 py-2 border-t border-[var(--br)] bg-[var(--sf2)] flex gap-2 overflow-x-auto" data-testid="product-suggestions">
          {productSuggestions.map((name) => (
            <button
              key={name}
              type="button"
              className="chip whitespace-nowrap capitalize"
              onClick={() => applyProductSuggestion(name)}
            >
              {name}
            </button>
          ))}
        </div>
      )}

      {/* Composer */}
      <div className="chat-composer flex gap-2 items-center">
        <VoiceButton
          disabled={sending}
          onTranscript={(t) => setText((prev) => (prev ? prev + " " + t : t))}
        />
        <div className="chat-input-wrap">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={
              ((() => {
                const last = [...messages].reverse().find((m) => m.role === "assistant");
                return last?.data?.pending_context?.step === "await_deposit_amount"
                  ? "Type the deposit amount (e.g. 25000)..."
                  : `Message ${accountantName}…`;
              })())
            }
            disabled={sending}
            data-testid="chat-input"
          />
        </div>
        <button
          onClick={() => send()}
          disabled={sending || !text.trim()}
          data-testid="send-btn"
          className="chat-send-btn"
          style={{
            background: text.trim() ? "var(--ac)" : "var(--sf2)",
            color: text.trim() ? "#fff" : "var(--tx3)",
            border: text.trim() ? "none" : "1px solid var(--br)",
          }}
        >
          <KipaIcon icon={Send} size="nav" color={text.trim() ? "#fff" : "var(--color-text-muted)"} />
        </button>
      </div>

      {payInvoice && (
        <RecordPaymentModal
          invoice={payInvoice}
          onClose={() => setPayInvoice(null)}
          onSaved={async () => {
            setPayInvoice(null);
            await loadMessages();
            if (onAfterSend) onAfterSend();
          }}
        />
      )}
    </div>
  );
}

function fireConfetti() {
  const defaults = { origin: { y: 0.75 }, zIndex: 999 };
  confetti({ ...defaults, particleCount: 60, spread: 70, colors: ["#0D6E6E", "#166534", "#C49A2A", "#ffffff"] });
  setTimeout(() => {
    confetti({ ...defaults, particleCount: 40, spread: 100, startVelocity: 35, colors: ["#0D6E6E", "#166534"] });
  }, 300);
}

// ════════════════════════════════════════════════════════════════════
// Dashboard
// ════════════════════════════════════════════════════════════════════
function Dashboard({ reloadKey, onOpenSalesWizard, onOpenExpenseWizard }) {
  const { fmt } = useAccountant();
  const [data, setData] = useState(null);
  const [position, setPosition] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [balances, setBalances] = useState(null);
  const [payInvoice, setPayInvoice] = useState(null);

  const load = async () => {
    try {
      const [d, ls, bal, pos] = await Promise.all([
        axios.get(`${API}/dashboard`),
        axios.get(`${API}/alerts/low-stock`),
        axios.get(`${API}/balances`),
        axios.get(`${API}/business/position`),
      ]);
      setData(d.data);
      setLowStock(ls.data);
      setBalances(bal.data);
      setPosition(pos.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
  }, [reloadKey]);

  if (!data) {
    return <div className="p-6 text-[var(--tx3)] text-center">Loading…</div>;
  }

  const { today, month, recent_transactions, unpaid_invoices, top_products } = data;

  return (
    <div className="p-4 pb-6 overflow-y-auto h-full" data-testid="dashboard-screen">
      {/* Hero profit card */}
      <div className="bg-[var(--ac)] rounded-3xl p-5 mb-3 relative overflow-hidden text-white" style={{ boxShadow: "0 8px 32px rgba(13,110,110,0.28)" }}>
        <div className="absolute -top-7 -right-7 w-36 h-36 rounded-full bg-white/10" />
        <div className="relative">
          <div className="text-[11px] font-bold uppercase tracking-[2px] text-white/70 mb-2">Today's Profit</div>
          <div className="text-[38px] font-black leading-none tracking-tight mb-4" data-testid="today-profit">
            <AnimatedCounter value={today.net_profit} format={(v) => fmt(v)} />
          </div>
          <div className="grid grid-cols-3 bg-white/10 rounded-2xl overflow-hidden">
            <div className="p-3 border-r border-white/15">
              <div className="text-[9px] font-bold uppercase tracking-wider text-white/65 mb-1">Sales</div>
              <div className="font-extrabold text-[14px]">{fmt(today.revenue)}</div>
            </div>
            <div className="p-3 border-r border-white/15">
              <div className="text-[9px] font-bold uppercase tracking-wider text-white/65 mb-1">Money Spent</div>
              <div className="font-extrabold text-[14px]">{fmt(today.expenses)}</div>
            </div>
            <div className="p-3">
              <div className="text-[9px] font-bold uppercase tracking-wider text-white/65 mb-1">Gross Profit</div>
              <div className="font-extrabold text-[14px]">{fmt(today.gross_profit ?? today.net_profit)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick record actions */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button
          type="button"
          onClick={() => onOpenSalesWizard?.()}
          data-testid="dashboard-record-sale"
          className="bg-white border border-[var(--br)] rounded-2xl p-3 text-left card-sh"
        >
          <div className="text-lg mb-1"><KipaIcon icon={Banknote} size="lg" color="var(--color-primary)" /></div>
          <div className="text-[13px] font-extrabold text-[var(--tx)]">Sell</div>
          <div className="text-[10px] text-[var(--tx3)]">Product, customer, payment</div>
        </button>
        <button
          type="button"
          onClick={() => onOpenExpenseWizard?.()}
          data-testid="dashboard-record-expense"
          className="bg-white border border-[var(--br)] rounded-2xl p-3 text-left card-sh"
        >
          <div className="text-lg mb-1"><KipaIcon icon={Receipt} size="lg" color="var(--color-error)" /></div>
          <div className="text-[13px] font-extrabold text-[var(--tx)]">Money spent</div>
          <div className="text-[10px] text-[var(--tx3)]">Fuel, rent, supplies…</div>
        </button>
      </div>

      {/* Balance Overview (Fix 3 — cash vs bank) */}
      <div className="bg-white border border-[var(--br)] rounded-2xl p-4 mb-3 card-sh" data-testid="balance-overview">
        <div className="flex justify-between items-center mb-3">
          <div className="text-[var(--tx)] font-extrabold text-[13px] flex items-center gap-1.5">
            <KipaIcon icon={Briefcase} size="sm" color="var(--color-primary)" />
            Balance Overview
          </div>
          <div className="text-[10px] text-[var(--tx3)] font-semibold uppercase tracking-wider">What you can spend</div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-[var(--sf2)] rounded-xl p-3 text-center" data-testid="balance-cash">
            <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--tx3)] flex items-center justify-center gap-1">
              <KipaIcon icon={Wallet} size="xs" /> Cash in Hand
            </div>
            <div className="font-extrabold text-[14px] text-[var(--tx)] mt-1">{fmt(balances?.cash_balance || 0)}</div>
          </div>
          <div className="bg-[var(--sf2)] rounded-xl p-3 text-center" data-testid="balance-bank">
            <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--tx3)] flex items-center justify-center gap-1">
              <KipaIcon icon={Landmark} size="xs" /> Bank Balance
            </div>
            <div className="font-extrabold text-[14px] text-[var(--tx)] mt-1">{fmt(balances?.bank_balance || 0)}</div>
          </div>
          <div className="bg-[var(--al)] rounded-xl p-3 text-center" data-testid="balance-total">
            <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--ac)] flex items-center justify-center gap-1">
              <KipaIcon icon={BarChart3} size="xs" color="var(--color-primary)" /> Total Available
            </div>
            <div className="font-extrabold text-[14px] text-[var(--ac)] mt-1">{fmt(balances?.total_available || 0)}</div>
          </div>
        </div>
      </div>

      {position && (
        <div className="bg-white border border-[var(--br)] rounded-2xl p-4 mb-3 card-sh" data-testid="business-position-card">
          <div className="text-[var(--tx)] font-extrabold text-[13px] mb-3">How your business is doing</div>
          <div className="grid grid-cols-2 gap-2 text-[12px]">
            <div className="bg-[var(--sf2)] rounded-xl p-2.5">
              <div className="text-[var(--tx3)] text-[10px] font-bold uppercase">People owing you</div>
              <div className="font-extrabold">{fmt(position.people_owing_you)}</div>
            </div>
            <div className="bg-[var(--sf2)] rounded-xl p-2.5">
              <div className="text-[var(--tx3)] text-[10px] font-bold uppercase">Stock value</div>
              <div className="font-extrabold">{fmt(position.stock_value)}</div>
            </div>
            <div className="bg-[var(--sf2)] rounded-xl p-2.5">
              <div className="text-[var(--tx3)] text-[10px] font-bold uppercase">You owe</div>
              <div className="font-extrabold">{fmt(position.total_you_owe)}</div>
            </div>
            <div className="bg-[var(--sf2)] rounded-xl p-2.5">
              <div className="text-[var(--tx3)] text-[10px] font-bold uppercase">Net profit (month)</div>
              <div className="font-extrabold">{fmt(position.net_profit_mtd)}</div>
            </div>
          </div>
        </div>
      )}

      {/* People owing you */}
      {unpaid_invoices.length > 0 && (
        <div className="bg-[var(--gdl)] border-[1.5px] border-[var(--gdd)] rounded-2xl p-3 mb-3" data-testid="unpaid-alert">
          <div className="text-[var(--go)] text-[12px] font-extrabold mb-2">
            People owing you · {fmt(today.unpaid_amount)} · {position?.people_owing_count || unpaid_invoices.length} people
          </div>
          {unpaid_invoices.slice(0, 5).map((inv) => {
            const bal = inv.total - (inv.amount_paid || 0);
            return (
              <div
                key={inv.id}
                className="bg-white/80 border border-[var(--gdd)] rounded-xl p-2.5 mb-2 last:mb-0 flex items-center gap-2"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-bold text-[var(--tx)] truncate">
                    {inv.invoice_number} · {inv.customer}
                  </div>
                  <div className="text-[11px] text-[var(--tx2)] capitalize truncate">
                    {inv.item} · owes {fmt(bal)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPayInvoice(inv)}
                  data-testid={`dash-pay-${inv.id}`}
                  className="px-3 py-2 rounded-xl bg-[var(--ac)] text-white text-[10px] font-extrabold whitespace-nowrap inline-flex items-center gap-1"
                >
                  <KipaIcon icon={CircleDollarSign} size="xs" color="#fff" />
                  Mark paid
                </button>
              </div>
            );
          })}
          {unpaid_invoices.length > 5 && (
            <div className="text-[10px] text-[var(--tx3)] mt-2">
              +{unpaid_invoices.length - 5} more under People tab
            </div>
          )}
        </div>
      )}

      {/* Low-stock alert */}
      {lowStock.length > 0 && (
        <div className="bg-[var(--rl)] border-[1.5px] border-[var(--rdim)] rounded-2xl p-3 mb-3" data-testid="low-stock-dashboard">
          <div className="text-[var(--rd)] text-[12px] font-extrabold flex items-center gap-1.5">
            <KipaIcon icon={AlertTriangle} size="sm" color="var(--color-error)" />
            Low Stock ({lowStock.length} item{lowStock.length > 1 ? "s" : ""})
          </div>
          <div className="text-[var(--tx2)] text-[12px]">
            {lowStock.slice(0, 3).map((i) => `${i.name} (${Number(i.stock_qty)} left)`).join(" · ")}
            {lowStock.length > 3 ? ` +${lowStock.length - 3} more` : ""}
          </div>
        </div>
      )}

      {/* Monthly */}
      <div className="bg-white border border-[var(--br)] rounded-3xl p-4 mb-3 card-sh">
        <div className="flex justify-between items-center mb-2">
          <div className="text-[var(--tx)] font-extrabold text-[15px]">This Month</div>
          <div className={`text-[12px] font-bold flex items-center gap-0.5 ${month.growth_vs_prev_month >= 0 ? "text-[var(--gn)]" : "text-[var(--rd)]"}`}>
            <KipaIcon icon={month.growth_vs_prev_month >= 0 ? ArrowUpRight : ArrowDownRight} size="sm" />
            {Math.abs(month.growth_vs_prev_month)}%
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-[var(--sf2)] rounded-xl p-2.5 text-center">
            <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--tx3)]">Revenue</div>
            <div className="font-extrabold text-[14px] text-[var(--gn)]">{fmt(month.revenue)}</div>
          </div>
          <div className="bg-[var(--sf2)] rounded-xl p-2.5 text-center">
            <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--tx3)]">Expenses</div>
            <div className="font-extrabold text-[14px] text-[var(--rd)]">{fmt(month.expenses)}</div>
          </div>
          <div className="bg-[var(--sf2)] rounded-xl p-2.5 text-center">
            <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--tx3)]">Net</div>
            <div className="font-extrabold text-[14px] text-[var(--tx)]">{fmt(month.net_profit)}</div>
          </div>
        </div>
      </div>

      {/* Top products */}
      {top_products.length > 0 && (
        <>
          <div className="text-[var(--tx3)] text-[11px] font-bold uppercase tracking-wider mb-2 mt-2 flex items-center gap-1.5">
            <KipaIcon icon={Trophy} size="sm" color="var(--color-gold)" />
            Top Products
          </div>
          <div className="bg-white border border-[var(--br)] rounded-2xl p-4 mb-3 card-sh">
            {top_products.map((p, i) => {
              const max = top_products[0].revenue || 1;
              const pct = (p.revenue / max) * 100;
              const colors = ["var(--gn)", "var(--bl)", "var(--go)", "var(--pu)", "var(--ac)"];
              const c = colors[i % colors.length];
              return (
                <div key={i} className={i < top_products.length - 1 ? "mb-3" : ""}>
                  <div className="flex justify-between items-center mb-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-lg flex items-center justify-center text-[11px] font-black" style={{ background: c + "20", color: c }}>
                        {i + 1}
                      </div>
                      <div className="text-[13px] font-bold text-[var(--tx)] capitalize">{p.name}</div>
                    </div>
                    <div className="text-[13px] font-extrabold" style={{ color: c }}>{fmt(p.revenue)}</div>
                  </div>
                  <div className="h-1.5 rounded bg-[var(--bg2)] overflow-hidden">
                    <div style={{ width: `${pct}%`, background: c, height: "100%" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Recent */}
      <div className="text-[var(--tx3)] text-[11px] font-bold uppercase tracking-wider mb-2 mt-2">Recent Activity</div>
      {recent_transactions.length === 0 && (
        <div className="text-center py-8 text-[var(--tx3)] text-[13px] leading-relaxed px-4">
          No activity yet. Your sales and expenses will appear here once you log them in Chat.
        </div>
      )}
      {recent_transactions.map((t) => {
        const isIn = t.type === "income";
        return (
          <div key={t.id} className="bg-white border border-[var(--br)] rounded-2xl p-3 mb-2 flex items-center gap-3 card-sh" data-testid={`txn-${t.id}`}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: isIn ? "var(--gl)" : "var(--rl)" }}>
              <KipaIcon icon={isIn ? ArrowUpRight : ArrowDownLeft} size="nav" color={isIn ? "var(--color-success)" : "var(--color-error)"} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-bold text-[var(--tx)] truncate">{t.description}</div>
              <div className="text-[11px] text-[var(--tx3)] capitalize">{t.category}{t.customer ? ` · ${t.customer}` : ""}</div>
            </div>
            <div className="font-black text-[15px]" style={{ color: isIn ? "var(--gn)" : "var(--rd)" }}>
              {isIn ? "+" : "-"}{fmt(t.amount)}
            </div>
          </div>
        );
      })}

      {payInvoice && (
        <RecordPaymentModal
          invoice={payInvoice}
          onClose={() => setPayInvoice(null)}
          onSaved={() => {
            setPayInvoice(null);
            load();
          }}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Ledger — full transaction list
// ════════════════════════════════════════════════════════════════════
function Ledger({ reloadKey }) {
  const { fmt } = useAccountant();
  const { name: accountantName } = useAccountant();
  const [txns, setTxns] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/transactions`).then((r) => setTxns(r.data)).catch(() => {});
  }, [reloadKey]);

  const filtered = txns.filter((t) => filter === "all" || t.type === filter);
  const totalIn = txns.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0);
  const totalOut = txns.filter((t) => t.type === "expense").reduce((s, t) => s + t.amount, 0);

  return (
    <div className="flex flex-col h-full bg-[var(--bg2)]" data-testid="ledger-screen">
      <div className="bg-white px-4 py-3 border-b border-[var(--br)] flex gap-2">
        <div className="flex-1 bg-[var(--sf2)] rounded-xl p-2 text-center">
          <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--tx3)]">Money In</div>
          <div className="font-extrabold text-[14px] text-[var(--gn)]">{fmt(totalIn)}</div>
        </div>
        <div className="flex-1 bg-[var(--sf2)] rounded-xl p-2 text-center">
          <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--tx3)]">Money Out</div>
          <div className="font-extrabold text-[14px] text-[var(--rd)]">{fmt(totalOut)}</div>
        </div>
        <div className="flex-1 bg-[var(--sf2)] rounded-xl p-2 text-center">
          <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--tx3)]">Net</div>
          <div className="font-extrabold text-[14px] text-[var(--tx)]">{fmt(totalIn - totalOut)}</div>
        </div>
      </div>

      <div className="flex gap-2 px-4 py-2 bg-white border-b border-[var(--br)]">
        {["all", "income", "expense"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`ledger-filter-${f}`}
            className={`chip ${filter === f ? "chip-active" : ""}`}
          >
            {f === "all" ? "All" : f === "income" ? "Sales" : "Expenses"}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {txns.length === 0 && (
          <EmptyState
            icon={<KipaIcon icon={ClipboardList} size="xl" color="var(--color-primary)" />}
            headline="No transactions yet"
            body={`Your transaction history will appear here after you log sales or expenses in Chat.`}
            button={`Open ${accountantName} →`}
            onAction={() => window.location.hash = "#chat"}
            testid="ledger-empty"
          />
        )}
        {filtered.length === 0 && txns.length > 0 && (
          <div className="text-center py-10 text-[var(--tx3)] text-[13px]">No entries match this filter.</div>
        )}
        {filtered.map((t) => {
          const isIn = t.type === "income";
          return (
            <div key={t.id} className="bg-white border border-[var(--br)] rounded-2xl p-3 mb-2 flex items-center gap-3 card-sh">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: isIn ? "var(--gl)" : "var(--rl)" }}>
                <KipaIcon icon={isIn ? ArrowUpRight : ArrowDownLeft} size="nav" color={isIn ? "var(--color-success)" : "var(--color-error)"} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-bold text-[var(--tx)] truncate">{t.description}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md" style={{ background: isIn ? "var(--gl)" : "var(--rl)", color: isIn ? "var(--gn)" : "var(--rd)" }}>
                    {t.category}
                  </span>
                  <span className="text-[11px] text-[var(--tx3)]">{formatAppDateShort(t.created_at)}</span>
                </div>
                {isIn && t.profit != null && t.cost_amount > 0 && (
                  <div className="text-[10px] text-[var(--gn)] font-semibold mt-0.5">
                    Profit {fmt(t.profit)}
                  </div>
                )}
              </div>
              <div className="font-black text-[15px]" style={{ color: isIn ? "var(--gn)" : "var(--rd)" }}>
                {isIn ? "+" : "-"}{fmt(t.amount)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Main App Shell
// ════════════════════════════════════════════════════════════════════
function App() {
  const { token, loading: authLoading, logout } = useAuth();
  const { settings, loading, settingsError, refresh } = useAccountant();
  const [tab, setTab] = useState(() => readSavedTab("dashboard"));
  const [reloadKey, setReloadKey] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const [showLoginIntentPrompt, setShowLoginIntentPrompt] = useState(false);
  const [showSalesWizard, setShowSalesWizard] = useState(false);
  const [showExpenseWizard, setShowExpenseWizard] = useState(false);
  const [chatBoot, setChatBoot] = useState(null);
  const [welcomeDone, setWelcomeDone] = useState(() => hasSeenWelcome());
  const [authMode, setAuthMode] = useState("login");

  const pendingOnboarding = hasPendingOnboarding();
  const needsOnboarding =
    pendingOnboarding || settings?.onboarding_complete === false;

  const showOnboarding = !!token && needsOnboarding;

  const handleLoginIntent = (intent) => {
    setShowLoginIntentPrompt(false);
    markFirstLoginComplete();
    setShowSettings(false);
    setChatBoot(null);
    if (intent === "inventory") {
      setTab("inventory");
    } else if (intent === "sales") {
      setShowSalesWizard(true);
    } else if (intent === "expenses") {
      setShowExpenseWizard(true);
    } else if (intent === "explore") {
      setTab("chat");
    } else {
      setTab("dashboard");
    }
  };

  // Persist active tab so browser reload stays on the same screen
  useEffect(() => {
    if (token && !showOnboarding) saveActiveTab(tab);
  }, [tab, token, showOnboarding]);

  // After login: always start on dashboard + show intent prompt (not on page refresh)
  useEffect(() => {
    if (!token) {
      setShowSettings(false);
      setShowLoginIntentPrompt(false);
      setShowSalesWizard(false);
      setTab("dashboard");
      clearSavedTab();
      setChatBoot(null);
      return;
    }
    if (loading || showOnboarding) return;
    if (consumeFreshLogin()) {
      setShowSettings(false);
      setTab("dashboard");
      saveActiveTab("dashboard");
      setChatBoot(null);
      if (isFirstLoginPending()) {
        setShowLoginIntentPrompt(true);
      }
    }
  }, [token, loading, showOnboarding]);

  // Auto-prompt for push permission once, ~1.5s after onboarding-complete reveal
  useEffect(() => {
    if (loading || showOnboarding) return;
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    const alreadyPrompted = localStorage.getItem("bizpa_push_prompted") === "1";
    if (alreadyPrompted) return;
    if (typeof Notification === "undefined" || Notification.permission !== "default") return;
    const t = setTimeout(() => setShowPushPrompt(true), 1500);
    return () => clearTimeout(t);
  }, [loading, showOnboarding]);

  if (authLoading || (token && loading && !pendingOnboarding)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--ac)]">
        <div className="text-white text-[14px] font-semibold">Loading…</div>
      </div>
    );
  }

  if (token && settingsError && !settings) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--ac)] px-6">
        <div className="text-center max-w-[320px]">
          <div className="text-white text-[18px] font-bold mb-2">Couldn&apos;t load your workspace</div>
          <div className="text-white/75 text-[14px] mb-6">
            Check your connection and try again, or sign in again.
          </div>
          <button
            type="button"
            onClick={() => refresh()}
            className="w-full bg-white text-[var(--ac)] rounded-2xl py-3.5 font-bold text-[14px] mb-3"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => logout()}
            className="w-full bg-transparent text-white/80 rounded-2xl py-3 font-semibold text-[13px]"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  if (!token) {
    if (!welcomeDone) {
      return (
        <div className="App min-h-screen flex justify-center bg-[var(--bg2)]">
          <div className="bizpa-frame w-full max-w-[430px] min-h-screen bg-[var(--bg)] flex flex-col">
            <PreAuthFlow
              onComplete={() => {
                markWelcomeSeen();
                setWelcomeDone(true);
                setAuthMode("register");
              }}
            />
          </div>
        </div>
      );
    }
    return (
      <div className="App min-h-screen flex justify-center bg-[var(--bg2)]">
        <div className="bizpa-frame w-full max-w-[430px] min-h-screen bg-[var(--bg)] flex flex-col">
          <AuthScreen initialMode={authMode} />
        </div>
      </div>
    );
  }

  if (showOnboarding) {
    return (
      <div className="App min-h-screen flex justify-center bg-[var(--bg2)]">
        <div className="bizpa-frame w-full max-w-[430px] min-h-screen bg-[var(--bg)] flex flex-col relative">
          <OnboardingFlow onComplete={async () => { await refresh(); }} />
        </div>
      </div>
    );
  }

  if (token && !settings) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--ac)] px-6">
        <div className="text-center max-w-[320px]">
          <div className="text-white text-[18px] font-bold mb-2">Couldn&apos;t load your workspace</div>
          <button
            type="button"
            onClick={() => refresh()}
            className="w-full bg-white text-[var(--ac)] rounded-2xl py-3.5 font-bold text-[14px] mt-4"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return <>
    <MainWorkspace
      tab={tab} setTab={setTab}
      reloadKey={reloadKey} setReloadKey={setReloadKey}
      showSettings={showSettings} setShowSettings={setShowSettings}
      chatBoot={chatBoot}
      onOpenSalesWizard={() => setShowSalesWizard(true)}
      onOpenExpenseWizard={() => setShowExpenseWizard(true)}
    />
    <SalesWizard
      open={showSalesWizard}
      onClose={() => setShowSalesWizard(false)}
      onRecorded={() => {
        setReloadKey((k) => k + 1);
        setTab("chat");
      }}
    />
    <ExpenseWizard
      open={showExpenseWizard}
      onClose={() => setShowExpenseWizard(false)}
      onRecorded={() => {
        setReloadKey((k) => k + 1);
        setTab("chat");
      }}
    />
    <LoginIntentPrompt
      open={showLoginIntentPrompt}
      businessName={settings?.owner_name || settings?.business_name}
      accountantName={settings?.accountant_name}
      onSelect={handleLoginIntent}
      onDismiss={() => handleLoginIntent("dashboard")}
    />
    <PushPromptSheet open={showPushPrompt} onClose={() => setShowPushPrompt(false)} />
  </>;
}

function MainWorkspace({ tab, setTab, reloadKey, setReloadKey, showSettings, setShowSettings, chatBoot, onOpenSalesWizard, onOpenExpenseWizard }) {
  const { settings, refresh } = useAccountant();
  const tabs = [
    { k: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { k: "chat", label: "Chat", icon: MessageCircle },
    { k: "reports", label: "Reports", icon: TrendingUp },
    { k: "ledger", label: "Ledger", icon: ClipboardList },
    { k: "customers", label: "People", icon: Users },
    { k: "inventory", label: "Stock", icon: Package },
  ];

  return (
    <div className="App min-h-screen flex justify-center bg-[var(--bg2)]">
      <div className="bizpa-frame w-full max-w-[430px] min-h-screen bg-[var(--bg)] flex flex-col relative">
        {/* App header */}
        <div className="bg-white px-4 py-3 flex items-center gap-3 border-b border-[var(--br)]">
          <button
            type="button"
            onClick={() => setShowSettings(true)}
            className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 border border-[var(--br)] bg-[var(--ac)] flex items-center justify-center"
            aria-label="Edit business logo"
            data-testid="header-logo"
          >
            {settings?.business_logo ? (
              <img src={settings.business_logo} alt="" className="w-full h-full object-cover" />
            ) : (
              <KipaIcon icon={Briefcase} size="nav" color="#fff" />
            )}
          </button>
          <div className="flex-1">
            <div className="text-[var(--tx)] font-extrabold text-[16px] leading-none" data-testid="app-business-name">
              {settings?.business_name || "KipaBiz"}
            </div>
            <div className="text-[var(--ac)] text-[11px] font-semibold mt-0.5">AI Business Workspace · Active</div>
          </div>
          <button
            onClick={() => setShowSettings(true)}
            data-testid="open-settings"
            className="w-10 h-10 rounded-xl bg-[var(--sf2)] border border-[var(--br)] flex items-center justify-center text-[var(--tx2)]"
          >
            <KipaIcon icon={Settings} size="nav" color="var(--color-text-secondary)" />
          </button>
        </div>

        <SetupReminderBanner onOpenSettings={() => setShowSettings(true)} />

        {/* Screen body */}
        <div className="flex-1 overflow-hidden" style={{ paddingBottom: "var(--content-bottom-padding)" }}>
          {showSettings ? (
            <SettingsScreen
              onClose={async () => {
                setShowSettings(false);
                await refresh();
                setReloadKey((k) => k + 1);
              }}
            />
          ) : (
            <>
              {tab === "chat" && (
                <ChatScreen
                  bootIntent={chatBoot}
                  chatReloadKey={reloadKey}
                  onOpenSalesWizard={onOpenSalesWizard}
                  onOpenExpenseWizard={onOpenExpenseWizard}
                  onAfterSend={() => setReloadKey((k) => k + 1)}
                />
              )}
              {tab === "dashboard" && (
                <Dashboard
                  reloadKey={reloadKey}
                  onOpenSalesWizard={onOpenSalesWizard}
                  onOpenExpenseWizard={onOpenExpenseWizard}
                />
              )}
              {tab === "reports" && <ReportsScreen reloadKey={reloadKey} />}
              {tab === "ledger" && <Ledger reloadKey={reloadKey} />}
              {tab === "customers" && <CustomersScreen reloadKey={reloadKey} />}
              {tab === "inventory" && <InventoryScreen reloadKey={reloadKey} />}
            </>
          )}
        </div>

        {/* Bottom nav */}
        <div className="kipa-bottom-nav fixed bottom-0 w-full max-w-[430px] bg-white border-t border-[var(--br)] flex z-20" style={{ left: "50%", transform: "translateX(-50%)" }}>
          {tabs.map((t) => {
            const active = t.k === tab;
            return (
              <button
                key={t.k}
                onClick={() => {
                  setShowSettings(false);
                  setTab(t.k);
                }}
                data-testid={`nav-${t.k}`}
                className="flex-1 py-2 flex flex-col items-center gap-0.5"
                style={{ background: "transparent", border: "none" }}
              >
                <KipaIcon icon={t.icon} size="nav" color={active ? "var(--color-primary)" : "var(--color-text-muted)"} />
                <span className="text-[8.5px] font-bold uppercase tracking-wide" style={{ color: active ? "var(--ac)" : "var(--tx3)" }}>
                  {t.label}
                </span>
                <span className="w-1 h-1 rounded-full" style={{ background: active ? "var(--ac)" : "transparent" }} />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default App;
